import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Facebook, Instagram, Send } from "lucide-react";
import { LOGO_URL, NETWORK_SITES } from "@/lib/data";
import { PoweredBySocialPulse, SocialPulseLink } from "@/components/site/SocialPulseLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

// Pinterest icon (lucide doesn't include it) — inline SVG in the same style
function PinterestIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 20l3-9" />
      <path d="M11 11c0-1.5 1-3.5 3.5-3.5S18 9 18 11c0 2.5-1.5 4-3.5 4-1 0-1.75-.5-2-1" />
    </svg>
  );
}

export default function Footer() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: (message.trim() || "Interested in FamilyTree.Team — please stay in touch."),
      });
      toast.success("Thank you — we'll write back soon.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <footer data-testid="site-footer" className="relative mt-16 md:mt-24">
      {/* Heritage divider */}
      <div className="max-w-3xl mx-auto px-6 pb-8">
        <div className="flourish" />
      </div>

      {/* Logo + tagline */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
        <img src={LOGO_URL} alt="FamilyTree.Team" className="h-24 md:h-32 w-auto mx-auto" />
        <PoweredBySocialPulse className="mt-2 font-sans-ui text-[11px] md:text-[12px] tracking-[0.16em] uppercase text-[color:var(--sepia-warm)]/85" />
      </div>

      {/* THE PULSE NETWORK banner — wooden sign */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-12 md:mt-16">
        <div className="flex items-center gap-6">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/40" />
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
            className="wood-sign text-[13px] md:text-[15px] tracking-[0.28em]"
            data-testid="footer-pulse-network"
          >
            THE PULSE NETWORK
          </motion.div>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/40" />
        </div>

        {/* Social discs */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="font-italic-serif italic text-lg text-[color:var(--sepia)]">Social Pulse</div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="wood-disc"
              data-testid="footer-social-facebook"
              aria-label="Facebook"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="wood-disc"
              data-testid="footer-social-instagram"
              aria-label="Instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.pinterest.com"
              target="_blank"
              rel="noopener noreferrer"
              className="wood-disc"
              data-testid="footer-social-pinterest"
              aria-label="Pinterest"
            >
              <PinterestIcon size={20} />
            </a>
          </div>
        </div>

        {/* Network sites */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs font-heritage-caps tracking-[0.16em] text-[color:var(--sepia-warm)]">
          {NETWORK_SITES.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline hover:text-[color:var(--rust)]"
            >
              {s.label.toUpperCase()}
            </a>
          ))}
        </div>
      </div>

      {/* Contact form + Legal */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-16 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div>
          <div className="overline text-[color:var(--sepia)] mb-3">§ Legal</div>
          <ul className="text-sm space-y-2 font-heritage-caps tracking-[0.12em]">
            <li><Link to="/privacy" className="link-underline text-[color:var(--ink)]" data-testid="footer-privacy">PRIVACY POLICY</Link></li>
            <li><Link to="/terms" className="link-underline text-[color:var(--ink)]" data-testid="footer-terms">TERMS</Link></li>
            <li><a href="/contact" className="link-underline text-[color:var(--ink)]" data-testid="footer-contact-link">CONTACT</a></li>
          </ul>
          <p className="mt-6 max-w-sm text-[13px] font-italic-serif italic text-[color:var(--ink-soft)]">
            "We build the shelves your stories deserve. Every family, every generation,
            every quiet detail — remembered."
          </p>
        </div>

        <form onSubmit={submit} data-testid="footer-contact-form" className="border border-[color:var(--ink-line)] rounded-2xl bg-[color:var(--bone-2)] p-6 md:p-8 shadow-sm">
          <div className="heritage-title text-2xl mb-4">Contact</div>
          <label className="block mb-3">
            <span className="overline">Name</span>
            <input
              data-testid="footer-contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-[color:var(--sepia)]/60 py-2 focus:outline-none focus:border-[color:var(--rust)]"
              required
            />
          </label>
          <label className="block mb-3">
            <span className="overline">Email</span>
            <input
              type="email"
              data-testid="footer-contact-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-[color:var(--sepia)]/60 py-2 focus:outline-none focus:border-[color:var(--rust)]"
              required
            />
          </label>
          <label className="block mb-4">
            <span className="overline">Message (optional)</span>
            <textarea
              rows={3}
              data-testid="footer-contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1 w-full bg-transparent border border-[color:var(--sepia)]/40 p-2 focus:outline-none focus:border-[color:var(--rust)] text-sm resize-none"
              placeholder="A few words about the family you're researching…"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="footer-contact-submit"
            className="btn-wood w-full justify-center disabled:opacity-60"
          >
            <Send size={14} /> {loading ? "Sending…" : "Submit"}
          </button>
        </form>
      </div>

      {/* Copyright bar */}
      <div className="mt-16 border-t border-[color:var(--sepia)]/30">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 numeric-index">
          <div>© {new Date().getFullYear()} FamilyTree.Team™ · A <SocialPulseLink>Social Pulse ®</SocialPulseLink> child site</div>
          <div className="font-italic-serif italic text-[color:var(--sepia)]">Connecting Generations. Preserving Legacies.</div>
        </div>
      </div>
    </footer>
  );
}
